/**
 * Modal de prévisualisation **éditable** (axe §2.4).
 *
 * Depuis la Phase 4 (lettre de motivation), le modal gère DEUX documents :
 *  - onglet « CV » (toujours présent, c'est le doc principal) ;
 *  - onglet « Lettre » (toggle visible quand une lettre est disponible
 *    ou quand un `onGenerateLetter` est fourni — l'utilisateur peut alors
 *    cliquer pour la générer à la demande).
 *
 * Architecture conservée :
 *  - chaque doc est un HTML autonome ré-injecté dans la même iframe via `srcdoc` ;
 *  - le PDF est l'impression native de l'iframe (`contentWindow.print()`),
 *    donc fonctionne pour les deux documents sans code spécifique ;
 *  - Word reste un export secondaire ; on a un bouton dédié par document.
 */
import { useEffect, useRef, useState } from 'react'
import { Download, X, RefreshCw, Loader2, FileText, Printer, Pencil, Mail, SlidersHorizontal, GitCompare, ShieldCheck, Sparkles, History, Star, Save, Check, AlertCircle, MessageCircleQuestionMark } from 'lucide-react'
import { REVISION_CHANNEL } from '@ai-job-fit/engine/revisionBridge'
import { isAiNetworkError } from '@ai-job-fit/engine/aiClient'
import { noopCapture, type CaptureFn, type TranslateFn } from './context'

import type { DiffStats, FidelityScore } from '@ai-job-fit/engine/resumeDiff'
import type { ChiffreNonSourcé } from '@ai-job-fit/engine/chiffres'

import {
  RESUME_TONES,
  RESUME_LENGTHS,
  LETTER_LENGTHS,
  OPTIMIZATION_LEVELS,
} from '@ai-job-fit/engine/prompts'
import {
  ACCENT_PALETTE,
  LETTER_HEADERS,
  paginatePreview,
  type LetterHeader,
  type ResumeTemplate,
  type SectionVisibility,
} from '@ai-job-fit/engine/resumeHtml'
import { printFrame } from './printFrame'
import RevisionPopover from './RevisionPopover'
import { phraseChangements, type Traduction } from './AuditBadge'
import { animerDocument } from './animationDocument'
import AiLoader from './AiLoader'

export type DocKind = 'cv' | 'letter'

/** Clé de mémorisation de l'indice d'édition (affiché une seule fois). */
const EDIT_TIP_KEY = 'lcc:editTipDismissed'

/** Settings pilotables depuis le panneau live (B1). Défini ici pour éviter
 *  une dépendance circulaire avec ContentApp. */
export interface LivePreviewSettings {
  template?: ResumeTemplate
  accentColor?: string
  sections?: SectionVisibility
  /** En-tête de la lettre (rien / bloc expéditeur / gros titre). Rendu, donc live. */
  letterHeader?: LetterHeader
  tone?: string
  length?: string
  optimization?: string
  /** Langue de sortie (Automatique / Français / Anglais). Pilotée dans le panneau. */
  language?: string
}

/** Un modèle du catalogue de Radar (issue #235), montré en vignette. */
export interface ModeleCatalogue {
  id: ResumeTemplate
  label: string
  /** Le CV rendu avec ce modèle, sans édition : la vignette est le vrai CV. */
  html: string
  /** Une phrase sous les vignettes quand ce modèle est choisi. */
  note?: string
  /** Pas ouvert par la formule : la vignette porte ce texte et appelle
   *  `onModeleVerrouille` au lieu de changer de modèle. */
  verrou?: string
  /** Pas encore prêt (« Ma mise en page » en préparation) : grisé, ce texte. */
  attente?: string
}

// Ordre d'affichage des sections masquables (seules les expériences sont
// toujours visibles). L'intro et les compétences sont désormais masquables.
const OPTIONAL_SECTIONS: Array<keyof SectionVisibility> = ['summary', 'skills', 'education', 'languages', 'projects']

export interface PreviewModalProps {
  /** HTML complet du CV (résultat de `renderResumeToHTML`, avec `editable`). */
  html: string

  // --- Lettre de motivation (Phase 4) ----------------------------------------
  /** HTML complet de la lettre. Non fourni -> onglet « Lettre » vide (déclenche la génération). */
  letterHtml?: string
  /** Callback pour générer la lettre à la demande. Si absent, l'onglet « Lettre » n'apparaît pas. */
  onGenerateLetter?: () => Promise<void> | void
  /** Indique qu'une génération est en cours (spinner sur le bouton « Générer »). */
  generatingLetter?: boolean
  /** Export Word de la lettre. Si absent, le bouton Word est désactivé sur l'onglet lettre. */
  onExportLetterWord?: () => Promise<void> | void
  /** Ajustement itératif de la lettre en langage libre (façon ChatGPT). Si absent
   *  (ou pas de lettre), la barre d'ajustement n'apparaît pas. */
  onRefineLetter?: (instruction: string) => Promise<void> | void
  /** Même barre sur l'onglet CV : régénère le CV en suivant la consigne (une
   *  nouvelle version). Absent, l'onglet CV n'a pas de barre : c'est le cas de
   *  l'extension, où seule la lettre s'ajuste. */
  onRefineCv?: (instruction: string) => Promise<void> | void
  /**
   * Reposer les questions d'écart pour le document affiché, puis le régénérer
   * avec les réponses (issue #166). L'hôte pose ses questions et régénère : le
   * composant ne fait que déclencher. Absent, pas de bouton.
   */
  onAskQuestions?: (doc: DocKind) => Promise<void> | void
  /**
   * Les précisions du candidat (faits d'enrichissement) que la génération
   * prend en compte, affichées repliées sous la barre : sans elles, on ne sait
   * pas ce que l'IA a lu (issue #166). Absent, rien n'est affiché.
   */
  factsInUse?: Array<{ topic: string; detail: string }>
  /**
   * Les chiffres du CV et de la lettre absents de toute source (issue #160,
   * `chiffresNonSourcés` du moteur, calculé par l'hôte qui connaît le profil
   * maître). Signalés pour le document affiché, jamais bloquants.
   */
  unsupportedNumbers?: { cv?: ChiffreNonSourcé[]; letter?: ChiffreNonSourcé[] }
  /** Libellés des versions successives de la lettre, dans l'ordre de création.
   *  Moins de 2 entrées -> pas de sélecteur (rien à choisir). */
  letterVersions?: string[]
  /** Index de la version actuellement affichée dans `letterVersions`. */
  letterVersionIndex?: number
  /** Sélection d'une version de l'historique. Sans ce callback, pas de sélecteur. */
  onSelectLetterVersion?: (index: number) => void

  // --- CV --------------------------------------------------------------------
  /** Export Word du CV (best-effort). Appelé quand l'utilisateur clique « Word ». */
  onExportWord: () => Promise<void> | void
  /** Appelé quand l'utilisateur ferme le modal sans exporter. */
  onClose: () => void

  /**
   * Enregistrement des retouches faites À LA MAIN dans l'aperçu.
   *
   * Reçoit le **document de l'iframe** : c'est à l'hôte de le récolter
   * (`harvestResume` du moteur) et de le persister — le composant partagé ne
   * connaît ni le profil ni son stockage.
   *
   * Fourni => bouton « Enregistrer », et **Word/PDF bloqués tant que des
   * modifications ne sont pas enregistrées**. Absent => comportement d'avant :
   * l'aperçu reste éditable, mais ce qu'on y tape ne va nulle part (c'est le
   * cas de l'extension, dont l'export repart du profil en mémoire).
   */
  onSave?: (doc: Document) => Promise<void> | void

  /**
   * Appelé quand un document vient d'être chargé dans l'iframe, avec son type.
   * Sert à l'hôte pour greffer ses propres commandes dans le document du moteur
   * (la console accroche un bouton « rétablir » sur chaque phrase reformulée de
   * la vue diff). Doit être idempotent : rappelé à chaque rechargement.
   */
  onDocumentReady?: (doc: Document, kind: 'cv' | 'letter' | 'diff') => void

  /**
   * Rend le composant DANS la page au lieu d'une modale par-dessus : pas de
   * fond assombri, pas de croix, pas de fermeture à Escape. Le reste — onglets,
   * bascule diff, réglages, actions — est identique, parce que c'est justement
   * le but : une seule surface d'édition, pas une version dégradée intégrée.
   */
  embedded?: boolean
  /** Libellés des versions successives du CV — même principe que pour la lettre. */
  cvVersions?: string[]
  /** Index de la version de CV actuellement affichée. */
  cvVersionIndex?: number
  /** Sélection d'une version de CV. Sans ce callback, pas de sélecteur sur l'onglet CV. */
  onSelectCvVersion?: (index: number) => void

  // --- Retouches IA ad-hoc (Phase 5) -----------------------------------------
  /**
   * Reçoit le fragment sélectionné + l'instruction de l'utilisateur, renvoie
   * le texte révisé (chaîne vide = échec silencieux, le popover affichera une erreur).
   * Si non fourni, la fonctionnalité de retouche est désactivée.
   */
  onRevise?: (selection: string, instruction: string) => Promise<string>

  // --- Panneau de réglages unifié (CV + lettre) ------------------------------
  /** Réglages du CV (rendu partagé + rédaction CV), pour initialiser les contrôles. */
  cvSettings?: LivePreviewSettings
  /** Réglages de rédaction de la LETTRE (le rendu template/accent reste partagé). */
  letterSettings?: LivePreviewSettings
  /** Paramètres de RENDU (template/couleur/sections) : appliqués en direct. */
  onChangeRenderSettings?: (partial: LivePreviewSettings) => void
  /** Paramètres de RÉDACTION (ton/longueur/ciblage/modèle/langue) : régénèrent le
   *  document actif (`doc`). CV -> ré-adaptation ; lettre -> régénération lettre. */
  onRegenerateWith?: (partial: LivePreviewSettings, doc: DocKind) => Promise<void> | void
  /**
   * La lettre affichée a été écrite depuis un CV qui a changé DEPUIS
   * (régénération, retour sur une version antérieure de l'historique). Elle
   * décrit alors un document que le recruteur ne recevra pas, et rien d'autre
   * ne le signale. On affiche un bandeau ; on ne régénère jamais tout seul —
   * ce serait un appel IA non demandé, sur une lettre peut-être retouchée.
   */
  letterStale?: boolean
  /** « La garder » : la lettre périmée vaut pour le CV affiché. Absent, seul
   *  « Réécrire la lettre » est proposé. */
  onKeepLetter?: () => Promise<void> | void
  /** « En faire mon réglage par défaut » : les réglages de rédaction choisis
   *  dans le panneau deviennent ceux des prochains documents (issue #179). */
  onMakeDefault?: (settings: LivePreviewSettings) => Promise<void> | void
  /**
   * Bascule automatiquement sur l'onglet Lettre dès qu'un `letterHtml` existe
   * AU MONTAGE (pas seulement quand il apparaît en cours de vie du composant).
   * Vrai par défaut : c'est le comportement historique de l'extension, testé
   * (rouvrir un job déjà traité montre la lettre déjà écrite). La console le
   * désactive (`false`) : `DossierAtelier` remonte un `PreviewModal` neuf à
   * chaque candidat sélectionné (`key={consultantId}`), et la plupart ont déjà
   * une lettre en base — sans ce réglage, cliquer un candidat après l'autre
   * fait sauter l'écran sur la lettre à chaque fois au lieu de rester sur le
   * CV, qui reste le document qu'on regarde en premier.
   */
  letterDefaultOnMount?: boolean
  /**
   * Le catalogue de modèles (Radar seulement, issue #235) : des vignettes en
   * tête du panneau, avec la couleur, au lieu de la paire ATS / Moderne repliée
   * sous « Mise en page ». Absent, rien ne change (extension, console).
   */
  modeles?: ModeleCatalogue[]
  /** Clic sur un modèle que la formule n'ouvre pas. */
  onModeleVerrouille?: (id: ResumeTemplate) => void
  /** Les couleurs proposées. Absent : `ACCENT_PALETTE`. */
  palette?: Array<{ hex: string; label: string }>
  /** « Toujours utiliser ce modèle » : le modèle et la couleur affichés
   *  deviennent ceux des prochains CV. Absent, pas de lien. */
  onModeleParDefaut?: () => Promise<void> | void
  /** Le Word ne suit pas la mise en page affichée (« Ma mise en page ») : le
   *  bouton le dit. */
  wordStandard?: boolean
  /** Sections du CV qui ont réellement du contenu. Une section absente d'ici
   *  (ou à false) n'est PAS proposée dans le panneau : inutile de masquer/afficher
   *  une section vide. Absent => toutes proposées (rétro-compatibilité). */
  availableSections?: Partial<Record<keyof SectionVisibility, boolean>>

  // --- Diff & transparence du travail IA (P3 — D1/D2/D3) ---------------------
  /** HTML du diff « avant / après » (annoté). Absent -> pas de toggle diff. */
  diffHtml?: string
  /** Compteur D1 des opérations IA (reformulées / ajoutées / retirées / réordonnées). */
  diffStats?: DiffStats
  /** Score D3 de fidélité au master (calcul local). */
  fidelity?: FidelityScore
  /** Le document est dans une autre langue que le CV maître. Sans appariement
   *  (`paired: false`), la phrase de changements ne donne aucun chiffre. */
  translation?: Traduction
  /** Demande de l'hôte d'afficher un document (le parcours de Radar ouvre
   *  l'onglet Lettre, issue #178). `n` change à chaque demande, même document
   *  compris : c'est lui qui déclenche, pas `doc`. */
  docDemande?: { doc: DocKind; n: number }

  // --- Fourni par l'app hôte (voir context.ts) -------------------------------
  /** Traduction des libellés. */
  t: TranslateFn
  /** Mesure d'usage. Absente => aucune mesure, le composant fonctionne pareil. */
  capture?: CaptureFn
  /** Lien « noter l'extension ». Absent => l'encart de notation n'apparaît pas
   *  (cas de la console, qui n'est pas distribuée sur un store). */
  storeUrl?: string
}

export default function PreviewModal({
  html,
  t,
  capture = noopCapture,
  storeUrl,
  letterHtml,
  onGenerateLetter,
  generatingLetter = false,
  onExportLetterWord,
  onRefineLetter,
  onRefineCv,
  onAskQuestions,
  factsInUse,
  unsupportedNumbers,
  letterVersions = [],
  letterVersionIndex = 0,
  onSelectLetterVersion,
  onExportWord,
  onClose,
  onSave,
  onDocumentReady,
  embedded = false,
  cvVersions = [],
  cvVersionIndex = 0,
  onSelectCvVersion,
  onRevise,
  cvSettings,
  letterSettings,
  onChangeRenderSettings,
  onRegenerateWith,
  letterStale = false,
  onKeepLetter,
  onMakeDefault,
  letterDefaultOnMount = true,
  availableSections,
  modeles,
  onModeleVerrouille,
  palette = ACCENT_PALETTE,
  onModeleParDefaut,
  wordStandard = false,
  diffHtml,
  diffStats,
  fidelity,
  translation,
  docDemande,
}: PreviewModalProps) {
  const [exportingWord, setExportingWord] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  // Retouches à la main non encore enregistrées (cf. `onSave`).
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | undefined>(undefined)
  const [activeDoc, setActiveDoc] = useState<DocKind>('cv')
  // La vue des changements s'allume une fois par ouverture de l'atelier (#184).
  const diffJoue = useRef(false)
  // Mode diff (P3) : remplace l'aperçu éditable par la vue annotée avant/après.
  const [showDiff, setShowDiff] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // --- Panneau de réglages unifié (CV + lettre) ---
  const [showSettings, setShowSettings] = useState(false)
  // Réglages de RENDU partagés (template/accent/sections) : poussés en direct.
  const liveSettings = cvSettings
  // Réglages de RÉDACTION : édités localement, appliqués au clic « Régénérer ».
  // Re-synchronisés au changement d'onglet car CV et lettre ont leurs propres
  // valeurs. La longueur a un set distinct par document (pages vs paragraphes).
  const initDraft = (s?: LivePreviewSettings, isLetter = false) => ({
    tone: s?.tone ?? 'Sobre',
    length: s?.length ?? (isLetter ? 'Standard' : 'Sans limite'),
    optimization: s?.optimization ?? 'Fidèle',
    language: s?.language ?? 'Automatique',
  })
  const [draft, setDraft] = useState(() => initDraft(cvSettings, false))
  // Confirmation unique du cran « Audacieux » (extrapolation assumée).
  const [audacieuxOk, setAudacieuxOk] = useState(false)
  // Encart de confirmation « Audacieux » intégré (remplace window.confirm) :
  // déplié tant que l'utilisateur n'a pas tranché.
  const [daringPrompt, setDaringPrompt] = useState(false)

  // Indice d'édition : affiché une fois, puis oublié (mémorisé côté navigateur).
  // Une fois vu, le répéter n'apprend plus rien et vole de la place au document.
  const [tipDismissed, setTipDismissed] = useState(() => {
    try { return localStorage.getItem(EDIT_TIP_KEY) === '1' } catch { return false }
  })
  const dismissTip = () => {
    setTipDismissed(true)
    try { localStorage.setItem(EDIT_TIP_KEY, '1') } catch { /* stockage indispo : tant pis */ }
  }

  // Ajustement libre de la lettre (façon ChatGPT) : texte de la demande en cours.
  const [letterInstruction, setLetterInstruction] = useState('')
  const [asking, setAsking] = useState(false)

  // État de la sélection courante dans l'iframe (Phase 5).
  // null = pas de sélection ; sinon = texte + position du bouton flottant.
  type Sel = { text: string; anchor: { x: number; y: number } }
  const [selection, setSelection] = useState<Sel | null>(null)
  const [revising, setRevising] = useState(false)
  const [revisionError, setRevisionError] = useState<string | undefined>(undefined)

  // Si l'utilisateur déclenche la génération de lettre depuis l'onglet CV,
  // on bascule automatiquement sur l'onglet « Lettre » quand le HTML arrive.
  //
  // `premierRendu` protège le cas `letterDefaultOnMount={false}` : cet effet
  // tourne aussi au tout premier rendu (React exécute les effets après le
  // montage), donc un `letterHtml` déjà présent AU MONTAGE (une lettre déjà
  // en base) basculerait sur Lettre avant même que l'utilisateur ait rien
  // demandé. On ne saute cette toute première exécution que si l'app hôte l'a
  // explicitement demandé ; la bascule « vient d'être générée » (les
  // exécutions suivantes, quand `letterHtml` change) n'est jamais affectée.
  const premierRendu = useRef(true)
  useEffect(() => {
    const premier = premierRendu.current
    premierRendu.current = false
    if (premier && !letterDefaultOnMount) return
    if (letterHtml) setActiveDoc('letter')
  }, [letterHtml])

  useEffect(() => {
    if (!docDemande || docDemande.n === 0) return
    setShowDiff(false)
    setActiveDoc(docDemande.doc)
  }, [docDemande?.n])

  // Cliquer sur l'onglet « Lettre » VAUT demande de génération : personne n'ouvre
  // cet onglet pour regarder une page vide. On lance donc la rédaction dès la
  // première visite, sans second clic sur un bouton.
  // `letterAttempted` mémorise qu'une tentative a eu lieu : ça évite de relancer
  // en boucle si la génération échoue (letterHtml reste vide), et ça permet de
  // ré-afficher le bouton comme filet de sécurité dans ce cas précis.
  const [letterAttempted, setLetterAttempted] = useState(false)
  useEffect(() => {
    if (activeDoc !== 'letter' || !onGenerateLetter) return
    if (letterHtml || generatingLetter || letterAttempted) return
    setLetterAttempted(true)
    void onGenerateLetter()
  }, [activeDoc, letterHtml, generatingLetter, letterAttempted, onGenerateLetter])

  // Re-synchronise les réglages de rédaction quand on bascule CV <-> lettre :
  // chaque document a ses propres ton/longueur/ciblage/modèle/langue. On ajuste
  // l'état pendant le rendu (pattern React recommandé) plutôt que via un effet.
  const prevDocRef = useRef<DocKind>(activeDoc)
  if (prevDocRef.current !== activeDoc) {
    prevDocRef.current = activeDoc
    setDraft(initDraft(activeDoc === 'letter' ? letterSettings : cvSettings, activeDoc === 'letter'))
  }

  // Charge le HTML approprié dans l'iframe selon l'onglet courant.
  // Lorsque l'onglet « Lettre » est sélectionné sans contenu, on affiche
  // une page placeholder pour conserver la même iframe (et donc le DOM).
  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) return
    if (activeDoc === 'letter') {
      const placeholder = generatingLetter ? t('preview.letter.writing') : t('preview.letter.empty')
      iframe.srcdoc = letterHtml ?? `<!doctype html><html><body style="font-family:system-ui;padding:24px;color:#666;">${placeholder}</body></html>`
    } else if (showDiff && diffHtml) {
      // Vue diff (P3) : read-only, annotée. On ne touche pas au `html` éditable.
      iframe.srcdoc = diffHtml
    } else {
      iframe.srcdoc = html
    }
    // Changement d'onglet ou de HTML -> on oublie la sélection (qui ne pointe
    // plus sur le bon document).
    setSelection(null)
    setRevisionError(undefined)

    const isCv = activeDoc === 'cv' && !(showDiff && diffHtml)
    const kind: 'cv' | 'letter' | 'diff' = activeDoc === 'letter' ? 'letter' : isCv ? 'cv' : 'diff'

    // Main tendue à l'hôte sur TOUS les documents (la console greffe ses
    // boutons « rétablir » dans la vue diff), donc avant la sortie anticipée
    // qui ne concerne que la pagination.
    const notifyReady = () => {
      const doc = iframe.contentDocument
      if (doc) onDocumentReady?.(doc, kind)
    }
    // Les changements s'allument, la lettre se pose : une fois par contenu
    // (#184). Au chargement seulement : l'appel immédiat ci-dessous peut encore
    // voir le document précédent, qui serait alors noté « déjà montré ».
    // La vue des changements ne joue qu'une fois par ouverture de l'atelier :
    // un « rétablir » recharge le diff, il ne doit pas rallumer tout le CV.
    const auChargement = () => {
      notifyReady()
      const doc = iframe.contentDocument
      if (!doc || kind === 'cv' || (kind === 'diff' && diffJoue.current)) return
      if (animerDocument(doc, kind, doc.body?.textContent ?? '') && kind === 'diff') diffJoue.current = true
    }
    iframe.addEventListener('load', auChargement)
    // Le document peut déjà être là (srcdoc identique, iframe déjà chargée).
    notifyReady()

    // Aperçu paginé en feuilles A4 (onglet CV seulement). FAIT DEPUIS LE PARENT :
    // l'iframe est sandboxée sans `allow-scripts`, donc un <script> embarqué ne
    // tournerait pas (et la CSP des sites hôtes le bloquerait). Le content script,
    // lui, a accès au DOM de l'iframe via `allow-same-origin`. Recalcul au
    // chargement, au resize (le panneau réglages change la largeur) et à l'édition.
    if (!isCv) return () => iframe.removeEventListener('load', auChargement)
    let timer: number | undefined
    const schedule = () => {
      if (timer) clearTimeout(timer)
      timer = window.setTimeout(() => {
        const doc = iframe.contentDocument
        if (doc) paginatePreview(doc)
      }, 50)
    }
    // Toute frappe dans le document marque le CV comme non enregistré. Le même
    // événement sert déjà à repaginer : c'est la seule source fiable, l'aperçu
    // étant modifiable partout à la fois.
    const onEdit = () => { schedule(); setDirty(true) }
    // Posé au chargement ET tout de suite : selon le navigateur et l'état du
    // cache, l'iframe peut être déjà prête quand l'effet tourne. N'attendre que
    // `load` laissait alors une preview éditable dont les frappes n'étaient
    // enregistrées nulle part — le retour exact du problème qu'on corrige.
    // (Ré-attacher le même handler sur le même document est sans effet.)
    const attach = () => { iframe.contentDocument?.addEventListener('input', onEdit, true) }
    const onLoad = () => { attach(); schedule() }
    iframe.addEventListener('load', onLoad)
    attach()
    const ro = new ResizeObserver(schedule)
    ro.observe(iframe)
    return () => {
      if (timer) clearTimeout(timer)
      iframe.removeEventListener('load', onLoad)
      iframe.removeEventListener('load', auChargement)
      ro.disconnect()
    }
  }, [html, letterHtml, activeDoc, showDiff, diffHtml, generatingLetter, onDocumentReady])

  // Un HTML fraîchement rendu (enregistrement, régénération, retouche IA) est
  // par construction à jour : il vient du profil stocké. On repart donc propre.
  useEffect(() => { setDirty(false) }, [html])

  // Bridge avec l'iframe : écoute les messages `revision:selection` (Phase 5).
  // Convertit les coordonnées (relatives à l'iframe) en coordonnées viewport
  // parent en ajoutant le rect de l'iframe.
  useEffect(() => {
    if (!onRevise) return
    const handler = (ev: MessageEvent) => {
      // Le content script partage l'objet `window` avec les scripts de la page
      // hôte (l'isolation porte sur les variables, pas sur les événements) :
      // n'importe quel script de LinkedIn/Indeed — une pub, un tag tiers
      // compromis — pouvait donc forger une fausse sélection, dont le texte
      // partait au LLM puis était réinséré dans le CV (audit M1). Seule
      // l'iframe de prévisualisation est une source légitime.
      if (ev.source !== iframeRef.current?.contentWindow) return
      const data = ev.data as any
      if (!data || data.channel !== REVISION_CHANNEL) return
      if (data.type !== 'revision:selection') return
      if (!data.hasSelection || typeof data.text !== 'string') {
        setSelection(null)
        return
      }
      const iframe = iframeRef.current
      if (!iframe) return
      const iframeRect = iframe.getBoundingClientRect()
      const rect = data.rect ?? { x: 0, y: 0, w: 0, h: 0 }
      // Place le bouton juste sous la sélection, dans les coordonnées viewport.
      const anchor = {
        x: iframeRect.left + rect.x,
        y: iframeRect.top + rect.y + rect.h + 6,
      }
      setSelection({ text: data.text, anchor })
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [onRevise])

  const handleRevise = async (instruction: string) => {
    if (!onRevise || !selection) return
    setRevising(true)
    setRevisionError(undefined)
    try {
      const revised = await onRevise(selection.text, instruction)
      if (!revised) {
        setRevisionError(t('revision.error.empty'))
        return
      }
      // Demande à l'iframe de remplacer la sélection mémorisée.
      iframeRef.current?.contentWindow?.postMessage(
        { channel: REVISION_CHANNEL, type: 'revision:replace', text: revised },
        '*'
      )
      setSelection(null)
    } catch (err) {
      // Le popover est étroit : on n'y déverse pas le message technique quand
      // la cause est un réseau filtré — l'explication actionnable suffit.
      const msg = isAiNetworkError(err)
        ? t('error.network')
        : err instanceof Error ? err.message : 'Erreur inconnue.'
      setRevisionError(msg)
    } finally {
      setRevising(false)
    }
  }

  const handleCancelRevision = () => {
    setSelection(null)
    setRevisionError(undefined)
    iframeRef.current?.contentWindow?.postMessage(
      { channel: REVISION_CHANNEL, type: 'revision:clear' },
      '*'
    )
  }

  // Fermeture par Escape — sans objet quand le composant vit dans la page :
  // il n'y a rien à refermer, et la touche appartient alors à la page hôte.
  useEffect(() => {
    if (embedded) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !exportingWord && !regenerating) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [embedded, onClose, exportingWord, regenerating])

  /**
   * Export PDF = impression native de l'iframe.
   * `contentWindow.print()` n'imprime QUE le document de l'iframe (le doc actif),
   * pas la page hôte. L'utilisateur choisit « Enregistrer en PDF » dans la boîte
   * de dialogue.
   */
  const handlePrintPdf = () => {
    const win = iframeRef.current?.contentWindow
    if (!win) return
    capture('export_pdf', { doc: activeDoc })
    // `printFrame` aligne le titre de la page hôte sur celui du document
    // imprimé : c'est de ce titre que Chrome dérive le nom du PDF proposé.
    printFrame(win)
  }

  const doExportWord = async () => {
    setExportingWord(true)
    try {
      if (activeDoc === 'letter') {
        await onExportLetterWord?.()
      } else {
        await onExportWord()
      }
    } finally {
      setExportingWord(false)
    }
  }

  // --- Panneau live (B1) : helpers ---
  const sections = liveSettings?.sections ?? {}
  const isSectionVisible = (k: keyof SectionVisibility) => sections[k] !== false
  const toggleSection = (k: keyof SectionVisibility, visible: boolean) =>
    onChangeRenderSettings?.({ sections: { ...sections, [k]: visible } })
  // On ne propose une section que si elle a du contenu (sinon rien à masquer).
  // Sans info `availableSections` (tests, rétro-compat), on les propose toutes.
  const offerableSections = OPTIONAL_SECTIONS.filter((k) => !availableSections || availableSections[k])

  // Régénère le document ACTIF avec les réglages de rédaction du panneau
  // (ton/longueur/ciblage/modèle/langue). Le parent route vers ré-adaptation
  // (CV) ou régénération de lettre selon `activeDoc`.
  const doRegenerateWith = async () => {
    if (!onRegenerateWith) return
    // Régénérer, c'est dire que le premier jet ne convenait pas : rapporté au
    // nombre de générations, c'est la mesure la plus directe de la qualité de
    // l'IA. L'event était déclaré dans la taxonomie depuis le début et n'avait
    // jamais été émis — un chiffre à zéro qui se lisait comme « personne ne
    // régénère », alors que personne ne comptait.
    capture('regenerate_clicked', { doc: activeDoc })
    setRegenerating(true)
    try {
      await onRegenerateWith({ ...draft }, activeDoc)
    } finally {
      setRegenerating(false)
    }
  }

  const busy = exportingWord || regenerating || generatingLetter || saving

  /**
   * Enregistre les retouches manuelles.
   *
   * On passe le DOCUMENT de l'iframe, pas du HTML : l'hôte récolte lui-même
   * (les marques `data-field` du moteur), stocke, et renvoie un `html` re-rendu
   * depuis ce qu'il a stocké. C'est ce qui garantit que le PDF imprimé plus tard
   * est le document en base, et pas une copie d'écran qui lui ressemble.
   */
  const doSave = async () => {
    const doc = iframeRef.current?.contentDocument
    if (!onSave || !doc) return
    setSaving(true)
    setSaveError(undefined)
    try {
      await onSave(doc)
      setDirty(false)
      capture('preview_saved')
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  // Modifications en attente : l'export produirait un document que personne ne
  // retrouverait ensuite. On bloque plutôt que d'exporter une version fantôme.
  const unsaved = !!onSave && dirty && activeDoc === 'cv'

  // Ajustement libre de la lettre : envoie la demande au parent puis vide le champ.
  // Sur l'onglet CV, la même barre régénère le CV (`onRefineCv`) : le champ
  // est commun, la consigne part au document affiché.
  const onRefine = activeDoc === 'letter' ? onRefineLetter : onRefineCv
  const submitLetterRefine = async () => {
    // Sur le CV, le champ vide refait le CV tel quel : c'est le seul bouton
    // qui le refait (issue #178). Une lettre, elle, ne s'ajuste pas sans consigne.
    if (!onRefine || busy || (activeDoc === 'letter' && !letterInstruction.trim())) return
    const instr = letterInstruction.trim()
    if (activeDoc === 'cv') setRegenerating(true)
    try {
      await onRefine(instr)
      setLetterInstruction('')
    } finally {
      if (activeDoc === 'cv') setRegenerating(false)
    }
  }

  const askQuestions = async () => {
    if (!onAskQuestions || busy || asking) return
    setAsking(true)
    try {
      await onAskQuestions(activeDoc)
    } finally {
      setAsking(false)
    }
  }

  // Onglet « Lettre » visible si on a un HTML ou un callback de génération.
  const hasLetterTab = !!(letterHtml || onGenerateLetter)

  const isLetter = activeDoc === 'letter'

  // Historique : chaque document a le sien, le sélecteur suit l'onglet actif.
  const versions = isLetter ? letterVersions : cvVersions
  const versionIndex = isLetter ? letterVersionIndex : cvVersionIndex
  const onSelectVersion = isLetter ? onSelectLetterVersion : onSelectCvVersion

  // Bouton de génération : masqué pendant la rédaction automatique initiale.
  // Visible s'il y a une lettre (« Régénérer ») ou si la tentative auto n'a rien
  // produit (échec) — dans ce cas il redevient un « Générer la lettre » de secours.
  const showLetterButton = !!letterHtml || (letterAttempted && !generatingLetter)

  // Sur l'onglet lettre sans contenu : le bouton Word et le PDF sont désactivés
  // (rien à exporter). On garde le bouton « Générer la lettre » à la place.
  const onLetterTabEmpty = activeDoc === 'letter' && !letterHtml

  // Diff (P3) : toggle dispo sur l'onglet CV uniquement, et seulement si le
  // parent a fourni un HTML de diff. `inDiff` = vue diff réellement affichée.
  const hasDiff = !!diffHtml && activeDoc === 'cv'
  const inDiff = hasDiff && showDiff
  // En vue diff (inspection read-only), l'export refléterait une vue annotée /
  // incohérente : on désactive PDF + Word tant que le diff est affiché.
  const exportDisabledByDiff = inDiff

  // Le bouton Word de l'onglet lettre n'a de sens que si on a un callback dédié.
  const wordDisabled =
    busy || onLetterTabEmpty || exportDisabledByDiff || unsaved || (activeDoc === 'letter' && !onExportLetterWord)

  // Panneau de réglages disponible sur les DEUX onglets (CV + lettre). Le contenu
  // s'adapte au document actif (sections = CV seulement ; le reste partagé).
  const hasSettingsPanel = !!onChangeRenderSettings
  // Set de longueurs selon le document : pages (CV) vs paragraphes (lettre).
  const lengthOptions = isLetter ? LETTER_LENGTHS : RESUME_LENGTHS

  // Sélection d'un cran de ciblage. « Audacieux » (extrapolation assumée) déplie
  // une confirmation intégrée au panneau au lieu d'une alerte système : on n'active
  // le cran qu'une fois l'utilisateur d'accord (confirmé une fois pour la session).
  const selectOptimization = (v: string) => {
    if (v === 'Audacieux' && !audacieuxOk) {
      setDaringPrompt(true)
      return
    }
    setDaringPrompt(false)
    setDraft((d) => ({ ...d, optimization: v }))
  }
  const confirmDaring = () => {
    setAudacieuxOk(true)
    setDaringPrompt(false)
    setDraft((d) => ({ ...d, optimization: 'Audacieux' }))
  }
  const cancelDaring = () => setDaringPrompt(false)

  // Intégré : le composant remplit son conteneur, la page hôte gère le reste.
  // Modal : fond assombri cliquable + rôle dialog.
  const shellProps = embedded
    ? { className: 'preview-modal-embed', 'data-testid': 'preview-modal' }
    : {
        className: 'preview-modal-backdrop',
        role: 'dialog',
        'aria-modal': true,
        'aria-label': t('preview.aria'),
        onClick: onClose,
        'data-testid': 'preview-modal',
      }

  return (
    <div {...(shellProps as any)}>
      <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
        {/* Barre d'outils unique : identité du document + actions transverses.
            Remplace l'empilement titre / onglets / fidélité / indice qui
            repoussait le document vers le bas. */}
        <div className="preview-toolbar">
          {hasLetterTab ? (
            <div className="preview-tabs" role="tablist">
              <button
                role="tab"
                type="button"
                aria-selected={activeDoc === 'cv'}
                className={`preview-tab ${activeDoc === 'cv' ? 'active' : ''}`}
                onClick={() => setActiveDoc('cv')}
                data-testid="preview-tab-cv"
              >
                <FileText className="w-3.5 h-3.5" /> {t('preview.tab.cv')}
              </button>
              <button
                role="tab"
                type="button"
                aria-selected={activeDoc === 'letter'}
                className={`preview-tab ${activeDoc === 'letter' ? 'active' : ''}`}
                onClick={() => setActiveDoc('letter')}
                data-testid="preview-tab-letter"
              >
                <Mail className="w-3.5 h-3.5" /> {t('preview.tab.letter')}
              </button>
            </div>
          ) : (
            <div className="preview-title">
              <FileText className="w-4 h-4" />
              <span>{t('preview.tab.cv')}</span>
            </div>
          )}

          <div className="preview-toolbar-spacer" />

          {/* Comparaison avant / après (onglet CV, si un diff est fourni). */}
          {hasDiff && (
            <button
              type="button"
              onClick={() => setShowDiff((v) => {
                if (!v) capture('diff_viewed')
                return !v
              })}
              className={`preview-chip ${inDiff ? 'on' : ''}`}
              aria-pressed={inDiff}
              data-testid="preview-diff-toggle"
            >
              <GitCompare className="w-3.5 h-3.5" />
              {inDiff ? t('preview.diff.hide') : t('preview.diff.show')}
            </button>
          )}

          {/* Réglages : ouvre le panneau glissant. */}
          {hasSettingsPanel && (
            <button
              type="button"
              onClick={() => setShowSettings((v) => !v)}
              className={`preview-chip ${showSettings ? 'on' : ''}`}
              aria-pressed={showSettings}
              data-testid="preview-settings-toggle"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              {t('preview.settings.toggle')}
            </button>
          )}

          {!embedded && (
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="preview-close"
              aria-label={t('preview.close')}
              data-testid="preview-close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Historique des versions du document actif : une régénération ou un
            ajustement raté ne doit pas coûter la version d'avant. Visible dès
            qu'il y a un choix à faire. */}
        {onSelectVersion && versions.length > 1 && (
          <div className="preview-versions" data-testid="preview-versions">
            <History className="w-3.5 h-3.5" />
            <span className="preview-versions-label">{t('preview.versions.label')}</span>
            {versions.map((label, i) => (
              <button
                key={i}
                type="button"
                disabled={busy}
                aria-pressed={i === versionIndex}
                className={`preview-version ${i === versionIndex ? 'active' : ''}`}
                onClick={() => onSelectVersion(i)}
                title={t('preview.versions.item', { n: i + 1, label }) + (i === versionIndex ? t('preview.versions.current') : '')}
                data-testid={`preview-version-${i}`}
              >
                {i + 1}
              </button>
            ))}
            {/* Le libellé de la version courante en clair : « 3 » tout seul ne dit
                pas à l'utilisateur ce qu'il regarde. */}
            <span className="preview-versions-current" data-testid="preview-versions-current">
              {versions[versionIndex]}
            </span>
          </div>
        )}

        {/* Ce qui a changé, en une phrase, sur l'onglet CV (issue #178). Elle
            remplace la puce « % de ton profil » et le bandeau orange de la vue
            des changements, qui répétaient le badge de l'hôte. */}
        {activeDoc === 'cv' && diffStats && (
          <div className="preview-changes" role="status" data-testid="preview-changes">
            <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" />
            <span>{phraseChangements(diffStats, fidelity, t, translation)}</span>
          </div>
        )}

        <div className="preview-body">
          {/* Indice d'édition : flotte sur le document, une seule fois. Fermé,
              il ne revient plus (mémorisé) — inutile de le répéter à chaque ouverture. */}
          {!tipDismissed && !inDiff && !onLetterTabEmpty && !(wordStandard && !isLetter) && (
            <div className="preview-edit-hint" data-testid="preview-edit-hint">
              <Pencil className="w-3.5 h-3.5" />
              <span>{activeDoc === 'letter' ? t('preview.editHint.letter') : t('preview.editHint.cv')}</span>
              <button
                type="button"
                className="preview-edit-hint-close"
                onClick={dismissTip}
                data-testid="preview-edit-hint-close"
              >
                {t('preview.editHint.ok')}
              </button>
            </div>
          )}

          {hasSettingsPanel && showSettings && (
            <div
              className="preview-settings-scrim"
              onClick={() => setShowSettings(false)}
              data-testid="preview-settings-scrim"
            />
          )}
          {hasSettingsPanel && showSettings && (
            <aside className="preview-settings" data-testid="preview-settings">
              <div className="preview-settings-head">
                <p className="preview-settings-title">
                  {t('preview.settings.title')}
                  <span className="preview-settings-doc">{isLetter ? t('preview.tab.letter') : t('preview.tab.cv')}</span>
                </p>
                <button
                  type="button"
                  className="preview-close"
                  onClick={() => setShowSettings(false)}
                  aria-label={t('preview.close')}
                  data-testid="preview-settings-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="preview-settings-scroll">
              {/* ===== Modèles (Radar, issue #235) =====
                  En tête, avant la rédaction : c'est ce qu'on vient changer le
                  plus souvent, et la vignette se juge d'un coup d'œil. Le CV
                  seulement : la lettre garde sa mise en page. */}
              {modeles && !isLetter && (
                <div className="preview-settings-group" data-testid="preview-modeles">
                  <p className="preview-settings-group-title">{t('preview.settings.group.layout')}</p>
                  <div className="preview-modeles" role="radiogroup" aria-label={t('preview.settings.template')}>
                    {modeles.map((m) => {
                      const actif = (liveSettings?.template ?? 'ATS-Optimized') === m.id
                      return (
                        <button
                          key={m.id}
                          type="button"
                          role="radio"
                          aria-checked={actif}
                          disabled={Boolean(m.attente)}
                          className={`preview-modele ${actif ? 'active' : ''} ${m.verrou ? 'verrou' : ''}`}
                          onClick={() => (m.verrou ? onModeleVerrouille?.(m.id) : onChangeRenderSettings?.({ template: m.id }))}
                          data-testid={`preview-modele-${m.id}`}
                        >
                          <span className="preview-modele-vue" aria-hidden="true">
                            {m.html ? <iframe srcDoc={m.html} sandbox="" tabIndex={-1} title="" loading="lazy" /> : null}
                          </span>
                          <span className="preview-modele-nom">
                            {actif && <Check className="w-3.5 h-3.5" aria-hidden="true" />}
                            {m.label}
                          </span>
                          {(m.verrou || m.attente) && <span className="preview-modele-etat">{m.verrou ?? m.attente}</span>}
                        </button>
                      )
                    })}
                  </div>
                  {modeles.find((m) => m.id === liveSettings?.template)?.note && (
                    <p className="preview-settings-explain">{modeles.find((m) => m.id === liveSettings?.template)?.note}</p>
                  )}
                  <div className="preview-settings-swatches preview-modeles-couleurs">
                    {palette.map(({ hex, label }) => (
                      <button
                        key={hex}
                        type="button"
                        title={label}
                        aria-label={label}
                        aria-pressed={(liveSettings?.accentColor ?? palette[0]?.hex) === hex}
                        className={`preview-swatch ${(liveSettings?.accentColor ?? palette[0]?.hex) === hex ? 'active' : ''}`}
                        style={{ backgroundColor: `#${hex}` }}
                        onClick={() => onChangeRenderSettings?.({ accentColor: hex })}
                        data-testid={`preview-accent-${hex}`}
                      />
                    ))}
                  </div>
                  {onModeleParDefaut && (
                    <button type="button" className="preview-modeles-defaut" onClick={() => void onModeleParDefaut()} data-testid="preview-modele-defaut">
                      {t('preview.settings.template.default')}
                    </button>
                  )}
                </div>
              )}

              {/* ===== Rédaction IA (relance la génération) ===== */}
              {onRegenerateWith && (
                <>
                  <p className="preview-settings-group-title">{t('preview.settings.group.writing')}</p>

                  <div className="preview-settings-group">
                    <label className="preview-settings-label">{t('preview.settings.language')}</label>
                    <div className="preview-settings-segmented">
                      {([['Automatique', 'auto'], ['Français', 'fr'], ['Anglais', 'en']] as const).map(([val, k]) => (
                        <button
                          key={val}
                          type="button"
                          disabled={busy}
                          className={`preview-seg ${draft.language === val ? 'active' : ''}`}
                          onClick={() => setDraft((d) => ({ ...d, language: val }))}
                          data-testid={`preview-lang-${val}`}
                        >
                          {t(`preview.settings.language.${k}`)}
                        </button>
                      ))}
                    </div>
                    <p className="preview-settings-explain">{t('preview.settings.language.hint')}</p>
                  </div>

                  <div className="preview-settings-group">
                    <label className="preview-settings-label">{t('preview.settings.optimization')}</label>
                    <div className="preview-settings-segmented wrap">
                      {OPTIMIZATION_LEVELS.map((v) => (
                        <button
                          key={v}
                          type="button"
                          disabled={busy}
                          className={`preview-seg ${draft.optimization === v ? 'active' : ''} ${v === 'Audacieux' ? 'daring' : ''}`}
                          onClick={() => selectOptimization(v)}
                          data-testid={`preview-opt-${v}`}
                        >
                          {t(`preview.settings.opt.${v}`)}
                        </button>
                      ))}
                    </div>
                    <p className={`preview-settings-explain ${draft.optimization === 'Audacieux' ? 'warn' : ''}`}>
                      {t(`preview.settings.opt.hint.${draft.optimization}`)}
                    </p>

                    {/* Confirmation « Audacieux » intégrée au panneau — remplace
                        l'alerte système : on n'active le cran qu'après accord. */}
                    {daringPrompt && (
                      <div className="preview-daring-confirm" data-testid="preview-daring-confirm">
                        <p>{t('preview.settings.opt.audacieux.confirm')}</p>
                        <div className="preview-daring-actions">
                          <button
                            type="button"
                            className="preview-daring-no"
                            onClick={cancelDaring}
                            data-testid="preview-daring-cancel"
                          >
                            {t('preview.settings.opt.audacieux.no')}
                          </button>
                          <button
                            type="button"
                            className="preview-daring-yes"
                            onClick={confirmDaring}
                            data-testid="preview-daring-confirm-yes"
                          >
                            {t('preview.settings.opt.audacieux.yes')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="preview-settings-group">
                    <label className="preview-settings-label">{t('preview.settings.tone')}</label>
                    <div className="preview-settings-segmented">
                      {RESUME_TONES.map((v) => (
                        <button
                          key={v}
                          type="button"
                          disabled={busy}
                          className={`preview-seg ${draft.tone === v ? 'active' : ''}`}
                          onClick={() => setDraft((d) => ({ ...d, tone: v }))}
                          data-testid={`preview-tone-${v}`}
                        >
                          {t(`preview.settings.tone.${v}`)}
                        </button>
                      ))}
                    </div>
                    <p className="preview-settings-explain">{t(`preview.settings.tone.hint.${draft.tone}`)}</p>
                  </div>

                  <div className="preview-settings-group">
                    <label className="preview-settings-label">{t('preview.settings.length')}</label>
                    <div className="preview-settings-segmented">
                      {lengthOptions.map((v) => (
                        <button
                          key={v}
                          type="button"
                          disabled={busy}
                          className={`preview-seg ${draft.length === v ? 'active' : ''}`}
                          onClick={() => setDraft((d) => ({ ...d, length: v }))}
                          data-testid={`preview-length-${v}`}
                        >
                          {t(`preview.settings.length.${v}`)}
                        </button>
                      ))}
                    </div>
                  </div>

                </>
              )}

              {/* ===== Mise en page (appliquée en direct) =====
                  APRÈS la rédaction, et repliée quand la rédaction est là : on
                  ouvre les réglages pour la langue, le niveau, le ton ; le
                  modèle et la couleur se règlent rarement. Ils passaient avant,
                  et la rédaction tombait sous le pli (analyse du 2026-10-15). */}
              <details className="preview-settings-layout" open={!onRegenerateWith} data-testid="preview-settings-layout">
                <summary className="preview-settings-group-title">{t('preview.settings.group.layout')}</summary>

              {!modeles && <div className="preview-settings-group">
                <label className="preview-settings-label">{t('preview.settings.template')}</label>
                <div className="preview-settings-segmented">
                  {(['ATS-Optimized', 'Moderne'] as ResumeTemplate[]).map((tpl) => (
                    <button
                      key={tpl}
                      type="button"
                      className={`preview-seg ${(liveSettings?.template ?? 'ATS-Optimized') === tpl ? 'active' : ''}`}
                      onClick={() => onChangeRenderSettings?.({ template: tpl })}
                      data-testid={`preview-template-${tpl}`}
                    >
                      {tpl === 'Moderne' ? t('preview.settings.template.modern') : t('preview.settings.template.ats')}
                    </button>
                  ))}
                </div>
              </div>}

              {!modeles && <div className="preview-settings-group">
                <label className="preview-settings-label">{t('preview.settings.accent')}</label>
                <div className="preview-settings-swatches">
                  {ACCENT_PALETTE.map(({ hex, label }) => (
                    <button
                      key={hex}
                      type="button"
                      title={label}
                      aria-label={label}
                      className={`preview-swatch ${(liveSettings?.accentColor ?? '0a66c2') === hex ? 'active' : ''}`}
                      style={{ backgroundColor: `#${hex}` }}
                      onClick={() => onChangeRenderSettings?.({ accentColor: hex })}
                      data-testid={`preview-accent-${hex}`}
                    />
                  ))}
                </div>
              </div>}

              {/* En-tête : LETTRE uniquement (le CV a toujours le sien). Réglage
                  de rendu, donc appliqué en direct : l'en-tête n'est pas du texte
                  écrit par le modèle, le régénérer pour le changer serait payer
                  un appel IA pour trois lignes de mise en page. */}
              {isLetter && (
                <div className="preview-settings-group">
                  <label className="preview-settings-label">{t('preview.settings.letterHeader')}</label>
                  <div className="preview-settings-segmented">
                    {LETTER_HEADERS.map((h) => (
                      <button
                        key={h}
                        type="button"
                        className={`preview-seg ${(liveSettings?.letterHeader ?? 'none') === h ? 'active' : ''}`}
                        onClick={() => onChangeRenderSettings?.({ letterHeader: h })}
                        data-testid={`preview-letterheader-${h}`}
                      >
                        {t(`preview.settings.letterHeader.${h}`)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Sections : CV uniquement (la lettre n'en a pas). On ne propose
                  que les sections qui ONT du contenu — masquer/afficher une
                  section vide (ex. projets) n'a aucun sens. */}
              {!isLetter && offerableSections.length > 0 && (
                <div className="preview-settings-group">
                  <label className="preview-settings-label">{t('preview.settings.sections')}</label>
                  {offerableSections.map((key) => (
                    <label key={key} className="preview-settings-check">
                      <input
                        type="checkbox"
                        checked={isSectionVisible(key)}
                        onChange={(e) => toggleSection(key, e.target.checked)}
                        data-testid={`preview-section-${key}`}
                      />
                      {t(`preview.settings.section.${key}`)}
                    </label>
                  ))}
                </div>
              )}

              </details>
              </div>

              {/* Pied de panneau : LE bouton de régénération (unique dans toute
                  la modale). La mise en page est déjà appliquée en direct ; ce
                  bouton relance l'IA avec le ton / la longueur / le ciblage. */}
              {onRegenerateWith && (
                <div className="preview-settings-foot">
                  <button
                    type="button"
                    className="preview-settings-apply"
                    onClick={doRegenerateWith}
                    disabled={busy}
                    data-testid="preview-settings-apply"
                  >
                    {regenerating ? <Loader2 className="w-4 h-4 spin" /> : <RefreshCw className="w-4 h-4" />}
                    {regenerating
                      ? t('preview.regenerating')
                      : isLetter ? t('preview.settings.applyLetter') : t('preview.settings.apply')}
                  </button>
                  {/* Ce qui se règle ici vaut pour CE document ; le lien en fait
                      le réglage de tous les suivants (issue #179). */}
                  {/* CV seulement : le panneau de la lettre a ses propres longueurs
                      (Courte, Standard), qui ne sont pas des longueurs de CV. */}
                  {onMakeDefault && !isLetter && (
                    <button
                      type="button"
                      className="preview-settings-default"
                      onClick={() => void onMakeDefault(draft)}
                      disabled={busy}
                      data-testid="preview-settings-default"
                    >
                      {t('preview.settings.makeDefault')}
                    </button>
                  )}
                  <p className="preview-settings-hint">{t('preview.settings.hint')}</p>
                </div>
              )}
            </aside>
          )}

          <iframe
            ref={iframeRef}
            title="Aperçu"
            className="preview-iframe"
            // allow-same-origin : accès au contentWindow ; allow-modals : autorise
            // la boîte d'impression depuis l'iframe sandboxée.
            sandbox="allow-same-origin allow-modals"
            data-testid="preview-iframe"
          />
          {/* Overlay pendant une génération IA : document estompé + loader
              animé, pour que l'attente ne ressemble jamais à un blocage. */}
          {(regenerating || generatingLetter) && (
            <AiLoader phase={regenerating ? 'adapt' : 'letter'} t={t} overlay />
          )}
        </div>

        {/* La lettre ne défend plus le CV qu'on va joindre : on le dit, on ne
            corrige pas à la place de l'utilisateur. */}
        {isLetter && letterStale && letterHtml && (
          <div className="preview-stale" role="status" data-testid="preview-letter-stale">
            <AlertCircle size={14} aria-hidden="true" />
            <span>{t('preview.letter.stale')}</span>
            {onGenerateLetter && (
              <button
                type="button"
                className="preview-stale-btn"
                disabled={busy}
                onClick={() => void onGenerateLetter()}
                data-testid="preview-letter-stale-regenerate"
              >
                {t('preview.letter.stale.regenerate')}
              </button>
            )}
            {onKeepLetter && (
              <button
                type="button"
                className="preview-stale-btn"
                disabled={busy}
                onClick={() => void onKeepLetter()}
                data-testid="preview-letter-stale-keep"
              >
                {t('preview.letter.stale.keep')}
              </button>
            )}
          </div>
        )}

        {(() => {
          const chiffres = (activeDoc === 'letter' ? (letterHtml ? unsupportedNumbers?.letter : undefined) : unsupportedNumbers?.cv) ?? []
          if (chiffres.length === 0 || inDiff) return null
          return (
            <div className="preview-numbers" role="status" data-testid="preview-numbers">
              <AlertCircle size={13} aria-hidden="true" />
              <span>
                {t('preview.numbers.warning')}{' '}
                {chiffres.map((c, i) => (
                  <span key={c.valeur}>
                    {i > 0 && ', '}
                    <strong title={c.contexte}>{c.valeur}</strong>
                  </span>
                ))}
              </span>
            </div>
          )
        })()}

        {/* Barre d'ajustement libre de la lettre (façon ChatGPT) — onglet Lettre. */}
        {/* Panneau de réglages ouvert : il a son propre « Refaire », la barre
            se tait pour ne pas en montrer deux l'un sous l'autre. */}
        {(onRefine || onAskQuestions) && (activeDoc === 'cv' || letterHtml) && !(hasSettingsPanel && showSettings) && (
          <div className="preview-refine" data-testid="preview-refine">
            {onAskQuestions && (
              <button
                type="button"
                className="preview-questions-btn"
                onClick={() => void askQuestions()}
                disabled={busy || asking}
                data-testid="preview-ask-questions"
              >
                {asking ? <Loader2 className="w-4 h-4 spin" /> : <MessageCircleQuestionMark className="w-4 h-4" />}
                {t('preview.questions.button')}
              </button>
            )}
            {onRefine && <>
            <input
              type="text"
              className="preview-refine-input"
              value={letterInstruction}
              disabled={busy}
              placeholder={t(activeDoc === 'cv' ? 'preview.cv.refine.placeholder' : 'preview.letter.refine.placeholder')}
              onChange={(e) => setLetterInstruction(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void submitLetterRefine() }}
              data-testid="preview-refine-input"
            />
            <button
              type="button"
              className="preview-refine-btn"
              onClick={() => void submitLetterRefine()}
              disabled={busy || (activeDoc === 'letter' && !letterInstruction.trim())}
              data-testid="preview-refine-submit"
            >
              {generatingLetter || regenerating ? <Loader2 className="w-4 h-4 spin" /> : <Sparkles className="w-4 h-4" />}
              {t(activeDoc === 'cv' ? 'preview.cv.refine.button' : 'preview.letter.refine.button')}
            </button>
            </>}
          </div>
        )}
        {/* Rien quand il n'y a aucune précision : « 0 précision(s) » ne disait
            rien qu'on ne voie déjà (issue #177). */}
        {factsInUse && factsInUse.length > 0 && (activeDoc === 'cv' || letterHtml) && (
          <details className="preview-facts" data-testid="preview-facts">
            <summary>{factsInUse.length === 1 ? t('preview.questions.facts.one') : t('preview.questions.facts', { n: factsInUse.length })}</summary>
            <ul>
              {factsInUse.map((f) => (
                <li key={f.topic}><strong>{f.topic}</strong> : {f.detail}</li>
              ))}
            </ul>
          </details>
        )}

        <footer className="preview-footer">
          {/* Pourquoi les exports sont grisés. Sans cette phrase, un bouton
              inerte passe pour un bug. */}
          {/* `role="status"` : la raison pour laquelle deux boutons viennent de
              se griser doit être ANNONCÉE, pas seulement affichée — sans quoi,
              au clavier, on tabule sur des boutons inertes sans explication. */}
          {saveError ? (
            <span className="preview-save-note err" role="status" data-testid="preview-save-error">
              <AlertCircle size={13} aria-hidden="true" /> {saveError}
            </span>
          ) : unsaved ? (
            <span className="preview-save-note" role="status" data-testid="preview-save-hint">
              <AlertCircle size={13} aria-hidden="true" /> {t('preview.save.unsaved')}
            </span>
          ) : /* Incitation douce à noter, au moment de satisfaction (un document
              vient d'être généré). Simple lien, jamais bloquant — et absent
              quand l'app hôte n'est pas distribuée sur un store. */
          storeUrl ? (
            <a
              href={storeUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => capture('rate_cta_clicked', { placement: 'preview' })}
              className="preview-rate-link"
              data-testid="preview-rate-link"
              title={t('preview.rate.title')}
            >
              <Star size={13} aria-hidden="true" /> {t('preview.rate.cta')}
            </a>
          ) : (
            <span />
          )}
          <div className="preview-actions">
            {/* La PREMIÈRE génération est automatique (cf. effet plus haut) : pas de
                bouton à ce moment-là. Il ne réapparaît que pour « Régénérer » une
                lettre existante, ou pour réessayer si la tentative auto a échoué.
                (La régénération du CV, elle, vit dans le panneau de réglages —
                un seul bouton pour toute la modale.) */}
            {onGenerateLetter && activeDoc === 'letter' && showLetterButton && (
              <button
                type="button"
                onClick={() => void onGenerateLetter()}
                disabled={busy}
                className="preview-btn-secondary"
                data-testid="preview-generate-letter"
              >
                {generatingLetter ? <Loader2 className="w-4 h-4 spin" /> : <Mail className="w-4 h-4" />}
                {generatingLetter ? t('preview.generatingLetter') : letterHtml ? t('preview.regenerateLetter') : t('preview.generateLetter')}
              </button>
            )}

            {/* Enregistrer passe AVANT les exports, dans l'ordre de lecture :
                c'est la condition pour y accéder, pas une option de plus. */}
            {onSave && activeDoc === 'cv' && (
              <button
                type="button"
                onClick={doSave}
                disabled={busy || !dirty || inDiff}
                className={dirty ? 'preview-btn-primary' : 'preview-btn-secondary'}
                data-testid="preview-save"
                title={dirty ? t('preview.save.title') : t('preview.save.nothing')}
              >
                {saving ? <Loader2 className="w-4 h-4 spin" /> : dirty ? <Save className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                {saving ? t('preview.save.saving') : dirty ? t('preview.save') : t('preview.save.done')}
              </button>
            )}

            <button
              type="button"
              onClick={doExportWord}
              disabled={wordDisabled}
              className="preview-btn-secondary"
              data-testid="preview-export-word"
              title={unsaved ? t('preview.save.first') : t('preview.exportWord.title')}
            >
              {exportingWord ? <Loader2 className="w-4 h-4 spin" /> : <Download className="w-4 h-4" />}
              {exportingWord ? t('preview.exporting') : t(wordStandard && !isLetter ? 'preview.exportWord.standard' : 'preview.exportWord')}
            </button>

            <button
              type="button"
              onClick={handlePrintPdf}
              disabled={busy || onLetterTabEmpty || exportDisabledByDiff || unsaved}
              className="preview-btn-primary"
              data-testid="preview-export-pdf"
              title={unsaved ? t('preview.save.first') : t('preview.exportPdf.title')}
            >
              <Printer className="w-4 h-4" />
              {t('preview.exportPdf')}
            </button>
          </div>
        </footer>
      </div>

      {/* Popover de retouche IA — flotte au-dessus du modal, ancré sur la sélection. */}
      {onRevise && selection && (
        <RevisionPopover
          t={t}
          selectionText={selection.text}
          anchor={selection.anchor}
          busy={revising}
          error={revisionError}
          onRevise={handleRevise}
          onCancel={handleCancelRevision}
        />
      )}
    </div>
  )
}
